import type {
  DocumentTypeInput,
  JobTypeFamily,
  JobTypeInput,
  RequiredDocumentTypeInput,
  TrailerTypeInput,
  TruckClassInput,
  ZoneGeoJsonPoint,
  ZoneGeoJsonPolygon,
  ZoneInput,
  ZoneKindInput,
} from '@chukua/common';
import { api } from '../../lib/api';

export interface TruckClass {
  id: string;
  code: string;
  name: string;
  typicalCapacityTonnes: number;
  isActive: boolean;
}

export interface TrailerType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface DocumentType {
  id: string;
  code: string;
  name: string;
  appliesTo: 'truck' | 'trailer' | 'driver';
  legalHardBlock: boolean;
  crossBorderOnly: boolean;
  typicalValidityDays: number;
  reminderLeadDays: number;
  isActive: boolean;
}

export interface RequiredDocumentType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface JobType {
  id: string;
  code: string;
  name: string;
  family: JobTypeFamily;
  isActive: boolean;
  requiredDocuments: { id: string; code: string; name: string }[];
}

export function listTruckClasses(): Promise<TruckClass[]> {
  return api<TruckClass[]>('/truck-classes');
}

export function createTruckClass(input: TruckClassInput): Promise<TruckClass> {
  return api<TruckClass>('/truck-classes', { method: 'POST', body: JSON.stringify(input) });
}

export function updateTruckClass(id: string, input: TruckClassInput): Promise<TruckClass> {
  return api<TruckClass>(`/truck-classes/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateTruckClass(id: string): Promise<void> {
  await api<void>(`/truck-classes/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateTruckClass(id: string): Promise<void> {
  await api<void>(`/truck-classes/${id}/reactivate`, { method: 'POST' });
}

export function listTrailerTypes(): Promise<TrailerType[]> {
  return api<TrailerType[]>('/trailer-types');
}

export function createTrailerType(input: TrailerTypeInput): Promise<TrailerType> {
  return api<TrailerType>('/trailer-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateTrailerType(id: string, input: TrailerTypeInput): Promise<TrailerType> {
  return api<TrailerType>(`/trailer-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateTrailerType(id: string): Promise<void> {
  await api<void>(`/trailer-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateTrailerType(id: string): Promise<void> {
  await api<void>(`/trailer-types/${id}/reactivate`, { method: 'POST' });
}

export function listDocumentTypes(): Promise<DocumentType[]> {
  return api<DocumentType[]>('/document-types');
}

export function createDocumentType(input: DocumentTypeInput): Promise<DocumentType> {
  return api<DocumentType>('/document-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateDocumentType(id: string, input: DocumentTypeInput): Promise<DocumentType> {
  return api<DocumentType>(`/document-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateDocumentType(id: string): Promise<void> {
  await api<void>(`/document-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateDocumentType(id: string): Promise<void> {
  await api<void>(`/document-types/${id}/reactivate`, { method: 'POST' });
}

export function listRequiredDocumentTypes(): Promise<RequiredDocumentType[]> {
  return api<RequiredDocumentType[]>('/required-document-types');
}

export function createRequiredDocumentType(input: RequiredDocumentTypeInput): Promise<RequiredDocumentType> {
  return api<RequiredDocumentType>('/required-document-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateRequiredDocumentType(id: string, input: RequiredDocumentTypeInput): Promise<RequiredDocumentType> {
  return api<RequiredDocumentType>(`/required-document-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateRequiredDocumentType(id: string): Promise<void> {
  await api<void>(`/required-document-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateRequiredDocumentType(id: string): Promise<void> {
  await api<void>(`/required-document-types/${id}/reactivate`, { method: 'POST' });
}

export function listJobTypes(): Promise<JobType[]> {
  return api<JobType[]>('/job-types');
}

export function createJobType(input: JobTypeInput): Promise<Omit<JobType, 'requiredDocuments'>> {
  return api<Omit<JobType, 'requiredDocuments'>>('/job-types', { method: 'POST', body: JSON.stringify(input) });
}

export function updateJobType(id: string, input: JobTypeInput): Promise<Omit<JobType, 'requiredDocuments'>> {
  return api<Omit<JobType, 'requiredDocuments'>>(`/job-types/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateJobType(id: string): Promise<void> {
  await api<void>(`/job-types/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateJobType(id: string): Promise<void> {
  await api<void>(`/job-types/${id}/reactivate`, { method: 'POST' });
}

export interface ZoneKind {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export function listZoneKinds(): Promise<ZoneKind[]> {
  return api<ZoneKind[]>('/zone-kinds');
}

export function createZoneKind(input: ZoneKindInput): Promise<ZoneKind> {
  return api<ZoneKind>('/zone-kinds', { method: 'POST', body: JSON.stringify(input) });
}

export function updateZoneKind(id: string, input: ZoneKindInput): Promise<ZoneKind> {
  return api<ZoneKind>(`/zone-kinds/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateZoneKind(id: string): Promise<void> {
  await api<void>(`/zone-kinds/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateZoneKind(id: string): Promise<void> {
  await api<void>(`/zone-kinds/${id}/reactivate`, { method: 'POST' });
}

export interface Zone {
  id: string;
  code: string;
  name: string;
  kind: string;
  country: string;
  isActive: boolean;
  polygon: ZoneGeoJsonPolygon | null;
  centroid: ZoneGeoJsonPoint | null;
}

export function listZones(): Promise<Zone[]> {
  return api<Zone[]>('/zones');
}

export function getZone(id: string): Promise<Zone> {
  return api<Zone>(`/zones/${id}`);
}

export function createZone(input: ZoneInput): Promise<Zone> {
  return api<Zone>('/zones', { method: 'POST', body: JSON.stringify(input) });
}

export function updateZone(id: string, input: ZoneInput): Promise<Zone> {
  return api<Zone>(`/zones/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateZone(id: string): Promise<void> {
  await api<void>(`/zones/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateZone(id: string): Promise<void> {
  await api<void>(`/zones/${id}/reactivate`, { method: 'POST' });
}

export function getZonesMapConfig(): Promise<{ tileKey: string | null }> {
  return api<{ tileKey: string | null }>('/zones/map-config');
}
